/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// ## SEO url changes ## — centralized URL helpers per /p/{slug}/{id}.html and /c/{slug}/{id}/ scheme.
// Spec-aligned signatures: toProductUrl(config, product) / toCategoryUrl(config, category).
// Slug source is driven by config.seo.productSlugProperty / config.seo.categorySlugProperty,
// with slugFromName(product.name) / category.id as interim fallbacks until SCAPI exposes
// first-class slug fields (TD-0323752 / TD-0323747).

import type { AppConfig } from '@/types/config';

// ---------------------------------------------------------------------------
// Internal: slug sanitization
// ---------------------------------------------------------------------------

/**
 * Sanitize an arbitrary string into a URL-safe slug.
 * Lowercases, converts whitespace to underscores, strips everything else.
 * Used as an interim fallback until SCAPI exposes a first-class slug field.
 */
export function slugFromName(name: string): string {
    return name
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_-]/g, '');
}

// ---------------------------------------------------------------------------
// Product URLs
// ---------------------------------------------------------------------------

/**
 * Extract the slug segment from a SCAPI slugUrl field.
 * SCAPI returns slugUrl in the form: https://{host}/s/{site}/{slug}/{id}.html
 * or the shorter SFRA form: https://{host}/{slug}/{id}.html
 * We extract the penultimate path segment (before the {id}.html segment).
 *
 * @example
 * slugFromSlugUrl('https://example.com/s/RefArch/en_US/mahoney-sofa/3100438.html') → 'mahoney-sofa'
 * slugFromSlugUrl('https://example.com/mahoney-sofa/3100438.html')                 → 'mahoney-sofa'
 */
function slugFromSlugUrl(slugUrl: string): string | undefined {
    try {
        const { pathname } = new URL(slugUrl);
        // pathname: /s/RefArch/en_US/mahoney-sofa/3100438.html  or  /mahoney-sofa/3100438.html
        const segments = pathname
            .replace(/\.html$/, '')
            .split('/')
            .filter(Boolean);
        // The ID is always the last segment; the slug is second-to-last
        if (segments.length >= 2) {
            return segments[segments.length - 2];
        }
    } catch {
        // not a valid URL — fall through
    }
    return undefined;
}

/**
 * Build a PDP URL in the form /p/{slug}/{productId}.html
 *
 * Slug source priority:
 *   1. `product.slugUrl` — BM-configured SEO URL returned by SCAPI (getProduct response).
 *      The slug segment is extracted from the path: /p/{slug}/{id}.html
 *   2. `product[config.seo.productSlugProperty]` — BM custom attribute (e.g. c_seoSlug)
 *   3. `product.name` — sanitized display name (interim fallback, TD-0323752)
 *   4. `product.id` / `productId` — last resort
 *
 * @param config  - Application config (reads `config.seo.productSlugProperty`)
 * @param product - Product or search-hit object; must contain at least `id` or `productId`
 */
export function toProductUrl(
    config: Pick<AppConfig, 'seo'>,
    product: { id?: string; productId?: string; name?: string; slugUrl?: string; [key: string]: unknown }
): string {
    const id = String(product.productId ?? product.id ?? '');

    // Priority 1 — slugUrl from SCAPI (set from BM URL Rules / pageURL)
    if (product.slugUrl) {
        const extracted = slugFromSlugUrl(product.slugUrl);
        if (extracted) {
            return `/p/${extracted}/${id}.html`;
        }
    }

    // Priority 2 — configurable BM custom attribute (e.g. c_seoSlug)
    const slugProp = config.seo?.productSlugProperty ?? 'name';
    const rawSlug = (product[slugProp] as string | undefined) || product.name || id;
    const slug = slugFromName(rawSlug);
    return `/p/${slug}/${id}.html`;
}

// ---------------------------------------------------------------------------
// Category URLs
// ---------------------------------------------------------------------------

/**
 * Build a category URL in the form /c/{slug}/{categoryId}/
 *
 * URL resolution priority (mirrors Site Genesis c_alternativeUrl logic):
 *   1. `category.c_alternativeUrl` — full BM-authored URL stored on the category object.
 *      When `config.features.allowOverrideCategoryUrls` is true the value is used as-is;
 *      otherwise it is passed through `rewriteLegacyCategoryUrl()` to convert any
 *      legacy `/category/{id}` paths to the new `/c/{id}/` scheme.
 *   2. `category[config.seo.categorySlugProperty]` — BM custom attribute slug
 *      (e.g. a dedicated slug-only attribute). Produces the two-segment `/c/{slug}/{id}/` form.
 *   3. `category.id` — last resort; produces the clean single-segment `/c/{id}/` form.
 *
 * @param config   - Application config (reads `config.seo` and `config.features`)
 * @param category - Category object; must contain at least `id`
 */
export function toCategoryUrl(
    config: Pick<AppConfig, 'seo' | 'features'>,
    category: { id?: string; [key: string]: unknown }
): string {
    const id = String(category.id ?? '');

    // Priority 1 — c_alternativeUrl (full BM-authored URL, mirrors Site Genesis behaviour)
    const alternativeUrl = category.c_alternativeUrl as string | undefined;
    if (alternativeUrl) {
        const allowOverride = config.features?.allowOverrideCategoryUrls ?? false;
        return allowOverride ? alternativeUrl : rewriteLegacyCategoryUrl(alternativeUrl);
    }

    // Priority 2 — configurable BM slug-only property (separate from c_alternativeUrl full URL override)
    const slugProp = config.seo?.categorySlugProperty ?? 'id';
    const rawSlug = (category[slugProp] as string | undefined) || undefined;

    // Only emit the two-segment /c/{slug}/{id}/ form when a genuine BM slug exists
    // and differs from the category ID. Otherwise use the clean /c/{id}/ form.
    const hasDistinctSlug = rawSlug && rawSlug !== id;
    if (hasDistinctSlug) {
        const slug = slugFromName(rawSlug);
        return `/c/${slug}/${id}/`;
    }

    // Priority 3 — fallback: generate from category ID
    return `/c/${id}/`;
}

// ---------------------------------------------------------------------------
// Route-loader parsers
// ---------------------------------------------------------------------------

/**
 * Extract the productId from a splat param coming from the _app.p.$.tsx route.
 * The product ID is always the last path segment; strips any .html suffix.
 *
 * @example
 * parseProductSplat('mahoney_sofa/3100438.html') // → '3100438'
 * parseProductSplat('mahoney_sofa/3100438')      // → '3100438'
 */
export function parseProductSplat(splat: string): string {
    const segments = splat.split('/');
    const last = segments[segments.length - 1] ?? '';
    return last.replace(/\.html$/, '');
}

/**
 * Extract the categoryId from a splat param coming from the _app.c.$.tsx route.
 * The category ID is always the last non-empty path segment.
 *
 * @example
 * parseCategorySplat('furniture/living-room/')  // → 'living-room'
 * parseCategorySplat('furniture/living-room/sofas/') // → 'sofas'
 */
export function parseCategorySplat(splat: string): string {
    const segments = splat.replace(/\/$/, '').split('/');
    return segments[segments.length - 1] ?? '';
}

// ---------------------------------------------------------------------------
// Legacy URL rewriter (BM-authored attributes)
// ---------------------------------------------------------------------------

/**
 * Rewrite a BM-stored URL from legacy /category/{id} or /product/{id} patterns
 * to the new /c/{id}/{id}/ and /p/{id}/{id}.html schemes.
 *
 * Used for custom attributes (c_alternativeUrl, c_navigationOverrideUrl,
 * c_navigationBannerLink) that were authored before this URL scheme change.
 *
 * ## SEO url changes ## — interim bridge until BM data is updated to new URL scheme.
 */
export function rewriteLegacyCategoryUrl(url: string): string {
    // /category/sofas-and-couches → /c/sofas-and-couches/
    if (url.startsWith('/category/')) {
        const id = url.slice('/category/'.length).replace(/\/?$/, '');
        return `/c/${id}/`;
    }
    // /product/1234567 → /p/1234567/1234567.html (interim, no slug available)
    if (url.startsWith('/product/')) {
        const id = url.slice('/product/'.length).replace(/\.html$/, '');
        return `/p/${id}/${id}.html`;
    }
    return url;
}
